import type { IVector3 } from '@seddi/math-v2';
import { type SeamComponent, type SeamProcess, StitchOrder } from '@seddi/scene';

import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type SeamRenderable,
  attachGarmentGeometry,
  detachGarmentGeometry,
  updateLinesGeometryFromScene,
} from '../renderables/seamRenderable';
import { create as createTape } from '../renderables/seamTapeRenderable';
import { updateColor as updateStitchColor } from '../renderables/stitchRenderable';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';
import { update as updateTape } from './seamTapeReconciler';

/**
 * Reconcile seam renderable.
 * @param renderable - Seam renderable.
 * @param nextState - Next state,
 * @param prevState - Previous state.
 * @param frame - Scene reconciler frame.
 */
export const update = (
  renderable: SeamRenderable,
  nextState: SeamComponent,
  prevState: SeamComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const {
    config,
    nextGarmentGeometry,
    nextUseGarmentGeometry,
    prevGarmentGeometry,
    prevUseGarmentGeometry,
    resources,
  } = frame;

  const diffProps = PropsDiff.diff(nextState, prevState);
  let dirty = false;

  // Create tape.
  if (diffProps.includes('tape') && nextState?.tape) {
    renderable.tape ??= createTape(renderable.id, renderable.parent);
  }

  // Update from garment geometry.
  if (nextGarmentGeometry !== prevGarmentGeometry) {
    if (prevGarmentGeometry) {
      detachGarmentGeometry(renderable, resources);
    }
    if (nextGarmentGeometry) {
      attachGarmentGeometry(renderable, nextState, nextGarmentGeometry, resources);
    }
    dirty = true;
  }

  // Update lines from scene geometry.
  {
    const isGarmentGeometryDirty = !nextUseGarmentGeometry && prevUseGarmentGeometry;
    const isPiecesGeometryDirty = frame.entities.get(renderable.id)!.isGeometryDirty;

    if (
      !nextUseGarmentGeometry &&
      (diffProps.includes('lines') || isGarmentGeometryDirty || isPiecesGeometryDirty)
    ) {
      updateLinesGeometryFromScene(renderable, nextState, frame.nextScene);
      dirty = true;
    }
  }

  // Update stitches.
  if (!config.hideStitches) {
    // Update stitches material.
    if (nextGarmentGeometry && diffProps.includes('process') && prevState) {
      const nextProcess = nextState.process;
      const prevProcess = prevState.process;
      for (const stitch of renderable.stitches) {
        const color = computeStitchColorDiff(nextProcess, prevProcess, stitch.order, stitch.name);
        color && updateStitchColor(stitch, color, resources);
      }
    }
  }

  // Update tape.
  if (nextUseGarmentGeometry && diffProps.includes('tape')) {
    dirty = updateTape(renderable.tape, nextState.tape, prevState?.tape, frame) || dirty;
  }

  return dirty;
};

const convertStitchOrderToProp = (order: StitchOrder): 'stitch' | 'stitchSec' => {
  if (order === StitchOrder.SECONDARY) return 'stitchSec';
  return 'stitch';
};

const computeStitchColorDiff = (
  nextState: SeamProcess,
  prevState: SeamProcess,
  order: StitchOrder,
  name: string,
): IVector3 | undefined => {
  const nextStitch = nextState[convertStitchOrderToProp(order)];
  const prevStitch = prevState[convertStitchOrderToProp(order)];
  const nextColor = nextStitch[nextStitch.kind][name].color;
  const prevColor = prevStitch[prevStitch.kind][name].color;
  if (nextColor !== prevColor) {
    return nextColor;
  }
  return undefined;
};
